import { createStore } from 'zustand/vanilla'
import type { NjBusNetwork } from '@traintopia/contracts'
import { api } from '../services/api'
import { registerNjBusRoutes } from '../ui/colors'

// ─────────────────────────────────────────────────────────────────────────────
// NJ Transit's static bus network: routes, stops and route lines.
//
// The rail store's shape exactly (see store/rail.ts): one lazy load, the first time anything
// asks — the map when the NJ Bus mode is picked, an alert bullet naming an NJ route. It is
// the biggest of these payloads (~2.8 MB raw, 16k stops), so it is never fetched on a
// session that doesn't look at New Jersey. Loading it REGISTERS the route numbers and colour
// with `ui/colors`; nothing NJ-coloured may draw before `network` is set, or a badge is
// cached printing the internal route id (`12`) instead of the number on the bus (`113`).
//
// The buses themselves arrive on the vehicle socket as `VehiclePosition`s with
// `type: 'njbus'`, into the transit store like every vehicle.
// ─────────────────────────────────────────────────────────────────────────────

interface NjBusState {
  network: NjBusNetwork | null
  loading: boolean
  /** The last load gave up — most often because this server has no NJ Transit credentials.
   *  Cleared by the next `ensureLoaded`, which tries again. */
  unavailable: boolean
  /** Load the network if it isn't loaded or loading. Safe to call on every filter change. */
  ensureLoaded: () => void
}

const RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 30_000]

export const useNjBusStore = createStore<NjBusState>((set, get) => {
  let attempt = 0
  const load = () => {
    set({ loading: true })
    api
      .getNjBusNetwork()
      .then((network) => {
        // The backend answers 503 with an EMPTY network when NJ Transit is unreachable or
        // not configured; treat that as a failure, not a loaded network with no buses.
        if (!network.routes.length) throw new Error('empty NJ Transit network')
        registerNjBusRoutes(network.routes)
        attempt = 0
        set({ network, loading: false, unavailable: false })
      })
      .catch(() => {
        if (attempt >= RETRY_DELAYS_MS.length) {
          attempt = 0
          set({ loading: false, unavailable: true })
          return
        }
        setTimeout(load, RETRY_DELAYS_MS[attempt++])
      })
  }
  return {
    network: null,
    loading: false,
    unavailable: false,
    ensureLoaded: () => {
      const s = get()
      if (s.network || s.loading) return
      set({ unavailable: false })
      load()
    },
  }
})
